import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useWorkspace } from './WorkspaceContext';
import { useRealtime } from './RealtimeContext';
import { setActiveSeasonDirtyLabels } from './seasonDirtyRegistry';
import { seasonKeys } from '@/pages/season/seasonQueries';
import { invalidateSeasonStandings, seasonStandingsEvent } from '@/pages/season/seasonRealtime';

type RegisterDirty = (key: string, dirty: boolean, label: string) => void;
type RegisterDraft = (key: string, active: boolean) => void;
interface SeasonRuntimeValue {
  workspaceId: string;
  workspaceChanged: boolean;
  acknowledgeWorkspaceChange(): void;
  registerDirty: RegisterDirty;
  registerDraft: RegisterDraft;
  realtimeStatus: 'connecting' | 'connected' | 'disconnected';
}
const SeasonRuntimeContext = createContext<SeasonRuntimeValue | null>(null);

export const SeasonRuntimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const client = useQueryClient();
  const location = useLocation();
  const requested = useWorkspace().currentWorkspace?.id ?? '';
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const activeRoute = normalizedPathname === '/season' || normalizedPathname.startsWith('/season/');
  const previous = useRef(requested);
  const [ready, setReady] = useState(requested);
  const [workspaceChanged, setWorkspaceChanged] = useState(false);
  const acknowledgeWorkspaceChange = useCallback(() => setWorkspaceChanged(false), []);
  const [, setDirty] = useState<Record<string, string>>({});
  const [drafts, setDrafts] = useState<Record<string, true>>({});
  const draftActiveRef = useRef(false);
  const queuedSeasons = useRef(new Set<number>());
  const { subscribe, status } = useRealtime();
  const registerDirty = useCallback<RegisterDirty>((key, value, label) => setDirty(current => {
    if (value && current[key] === label) return current;
    if (!value && !(key in current)) return current;
    const next = { ...current };
    if (value) next[key] = label; else delete next[key];
    const nextLabels = Array.from(new Set(Object.values(next)));
    setActiveSeasonDirtyLabels(nextLabels);
    return next;
  }), []);
  const registerDraft = useCallback<RegisterDraft>((key, active) => setDrafts(current => {
    if (active && current[key]) return current;
    if (!active && !current[key]) return current;
    const next = { ...current };
    if (active) next[key] = true; else delete next[key];
    draftActiveRef.current = Object.keys(next).length > 0;
    return next;
  }), []);
  const workspaceId = activeRoute && ready === requested ? requested : '';

  useLayoutEffect(() => {
    let mounted = true;
    const switchWorkspace = async () => {
      const old = previous.current;
      setReady('');
      if (old && old !== requested) {
        setWorkspaceChanged(true);
        queuedSeasons.current.clear();
        await client.cancelQueries({ queryKey: seasonKeys.all(old) });
        client.removeQueries({ queryKey: seasonKeys.all(old) });
      }
      previous.current = requested;
      if (mounted) setReady(requested);
    };
    void switchWorkspace();
    return () => { mounted = false; };
  }, [client, requested]);

  useEffect(() => workspaceId ? subscribe(event => {
    const season = seasonStandingsEvent(workspaceId, event);
    if (!season) return;
    if (draftActiveRef.current) {
      queuedSeasons.current.add(season);
      return;
    }
    void invalidateSeasonStandings(client, workspaceId, season);
  }) : undefined, [client, subscribe, workspaceId]);
  useEffect(() => {
    if (Object.keys(drafts).length > 0 || !workspaceId || queuedSeasons.current.size === 0) return;
    const pending = Array.from(queuedSeasons.current);
    queuedSeasons.current.clear();
    pending.forEach(season => { void invalidateSeasonStandings(client, workspaceId, season); });
  }, [client, drafts, workspaceId]);
  useEffect(() => () => { queuedSeasons.current.clear(); setActiveSeasonDirtyLabels([]); }, []);

  return <SeasonRuntimeContext.Provider value={{ workspaceId, workspaceChanged, acknowledgeWorkspaceChange, registerDirty, registerDraft, realtimeStatus: status }}>
    {children}
  </SeasonRuntimeContext.Provider>;
};

export const useSeasonRuntime = (): SeasonRuntimeValue => {
  const value = useContext(SeasonRuntimeContext);
  if (!value) throw new Error('useSeasonRuntime must be used within SeasonRuntimeProvider');
  return value;
};

export const useSeasonDirtyGuard = (key: string, dirty: boolean, label: string): void => {
  const { registerDirty } = useSeasonRuntime();
  useEffect(() => {
    registerDirty(key, dirty, label);
    return () => registerDirty(key, false, label);
  }, [dirty, key, label, registerDirty]);
};

export const useSeasonDraftGuard = (key: string, active: boolean): void => {
  const { registerDraft } = useSeasonRuntime();
  useEffect(() => {
    registerDraft(key, active);
    return () => registerDraft(key, false);
  }, [active, key, registerDraft]);
};
